import { isLanguageCode } from '@sparted/shared-library/business/types';
import { t } from 'i18next';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { translateApiErrors } from 'Services/api-error/api-error.service';
import { activityActions } from 'Store/entities/activity/activity.slice';
import type { Campaign as CampaignType, PartialContentForCampaignForm } from 'Store/entities/activity/activity.type';
import { convertPartialContentForCampaignForm } from 'Store/entities/content/content.utils';

import { audienceFormatterFromStore } from '../AudienceForm/audience.utils';
import { SaveAudienceModal } from '../AudienceForm/SaveAudienceModal/SaveCampaignAudienceModal';
import { ActionBar } from './components/ActionBar/ActionBar';
import { Audience } from './components/tabs/Audience/Audience';
import { Overview } from './components/tabs/Overview/Overview';
import { Scheduling } from './components/tabs/Scheduling/Scheduling';
import { withFetchCampaignHoc } from './hoc/withCampaign';
import { applyTabChangeToRoute, inferInitialTabIndexFromPath } from './routing.utils';
import { CampaignFormValues, useCampaignSave } from './saveCampaign.hook';

const TAB_MAPPING: Record<number, React.FC> = {
  0: Overview,
  1: Scheduling,
  2: Audience,
};

export type CampaignProps = {
  campaign: CampaignType;
};

type InferFunctionArguments<T extends (...args: any[]) => any> = T extends (...args: infer U) => any ? U : never;

export const Campaign: React.FC<CampaignProps> = withFetchCampaignHoc(({ campaign }) => {
  const dispatch = useAppDispatch();

  const [visibleSaveModal, setVisibleSaveModal] = useState(false);

  const defaultValues = useMemo(() => {
    const orderedSessions: PartialContentForCampaignForm[][] = campaign.scheduling?.orderedSessions?.map((session) =>
      session.map(convertPartialContentForCampaignForm),
    ) || [[]];

    const contents = campaign.scheduling?.contents?.map(convertPartialContentForCampaignForm) || undefined;

    return {
      type: campaign?.scheduling?.type || 'flexible',
      mode: campaign?.scheduling?.mode || 'manual',
      name: campaign?.informations?.name || undefined,
      coverImage: campaign.informations.coverImage || undefined,
      description: campaign.informations.description || undefined,
      disclaimer: campaign.informations.disclaimer || undefined,
      launchDate: campaign?.scheduling?.launchDate || undefined,
      endDate: campaign?.scheduling?.endDate || undefined,
      extraDays: campaign.scheduling?.extraDays ?? 0,
      enabledWeekDays: campaign.scheduling?.enabledWeekDays || undefined,
      disabledDates: campaign.scheduling?.disabledDates || undefined,
      nbSessions: campaign.scheduling?.nbSessions || undefined,
      orderedSessions,
      contents,
      audiences: audienceFormatterFromStore(campaign?.audiences),
      audienceDirty: false,
      audienceTypeChanged: false,
    };
  }, [campaign]);

  const { setValue, watch, setError, clearErrors, reset, ...restOfFormMethods } = useForm<CampaignFormValues>({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [currentTabIndex, setCurrentTabIndex] = useState(() => inferInitialTabIndexFromPath());
  const tabs = [t('activities:tabs.overview'), t('activities:tabs.scheduling'), t('activities:tabs.audience')];

  const Tab = TAB_MAPPING[currentTabIndex];

  const { locked, saving, selectedLanguage } = useTypedSelector((state) => state.entities.activity.edition);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const languageParam = urlParams.get('language');

    if (languageParam && isLanguageCode(languageParam)) {
      if (campaign.languages.includes(languageParam)) {
        dispatch(activityActions.setEditionLanguage(languageParam));
      }
    }
  }, [campaign.languages, dispatch]);

  const contentValidationStatus = { total: 0, toTranslate: 0, toValidate: 0 };

  const campaignLastSave = campaign.informations.lastSave
    ? moment(campaign.informations.lastSave).calendar({
        sameDay: `[${t('activities:dates.same_day_save')}] LT`,
        nextDay: '',
        nextWeek: '',
        lastDay: `[${t('activities:dates.last_day_save')}] LT`,
        lastWeek: `[${t('activities:dates.last_save_long_date')}] DD/MM/YYYY`,
        sameElse: `[${t('activities:dates.last_save_long_date')}] DD/MM/YYYY`,
      })
    : '';

  const { handleCampaignSave, handleCampaignAudienceSave, handleCampaignLive } = useCampaignSave({
    reset,
    watch,
    setVisibleSaveModal,
    campaign,
    dirty: restOfFormMethods.formState.isDirty,
    defaultValues,
    clearErrors,
  });

  const { error } = useTypedSelector((state) => state.entities.activity.edition);

  useEffect(() => {
    if (error === null || typeof error === 'string') {
      return;
    }

    const informationsError = error?.[selectedLanguage]?.informations || error?.informations;
    if (typeof informationsError !== 'object' || Array.isArray(informationsError) || informationsError === null) {
      return;
    }

    const informationsErrorKeys = Object.keys(informationsError) as (keyof typeof informationsError)[];
    informationsErrorKeys.map((key) => {
      const errorMessage = informationsError[key];

      if (!errorMessage || !(key in defaultValues)) {
        return;
      }

      setError(key as keyof typeof defaultValues, {
        message: translateApiErrors({ [key]: errorMessage }),
      });
    });
  }, [error, selectedLanguage]);

  return (
    <FormProvider
      {...restOfFormMethods}
      setValue={(...args: InferFunctionArguments<typeof setValue>) => {
        const [name, value, config] = args;
        setValue(name, value, {
          ...config,
          shouldDirty: true,
        });
      }}
      watch={watch}
      setError={setError}
      clearErrors={clearErrors}
      reset={reset}
    >
      <ActionBar
        disableSave={locked || (!restOfFormMethods.formState.isDirty && !watch('audienceDirty'))}
        tabs={tabs}
        currentTabIndex={currentTabIndex}
        campaignId={campaign.id}
        campaignContentStatus={contentValidationStatus}
        campaignStatus={campaign.status}
        campaignName={watch(`name.${selectedLanguage}`) || ''}
        campaignLastSave={campaignLastSave}
        campaignLanguages={campaign.languages}
        selectedLanguage={selectedLanguage}
        locked={locked}
        onTabChange={(newTabIndex) => {
          applyTabChangeToRoute(newTabIndex);
          setCurrentTabIndex(newTabIndex);
        }}
        onLockChange={(lock) => dispatch(activityActions.setEditionLock(lock))}
        onLanguageChange={(newSelectedLanguage) => {
          dispatch(activityActions.setEditionLanguage(newSelectedLanguage));
        }}
        onSave={handleCampaignSave}
        onLive={handleCampaignLive}
        saving={saving}
        isFormDirty={restOfFormMethods.formState.isDirty}
      />
      <Tab />

      {visibleSaveModal && (
        <SaveAudienceModal
          scope="campaign"
          onAction={(validated) => {
            if (validated) {
              handleCampaignAudienceSave(watch('audiences'), watch('audienceTypeChanged'), campaign.audiences);
            } else {
              setVisibleSaveModal(false);
            }
          }}
        />
      )}
    </FormProvider>
  );
});
